import { type AuthState, toRelativeUrl } from '@okta/okta-auth-js';
import { DefaultPending } from '@sportybot-web/components/default-pending';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import type { SportybotGroup } from './permissions/permissions';

import { router } from '../router';
import { authClient } from './client';
import { AuthContext } from './context';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    accessToken: undefined,
    error: undefined,
    idToken: undefined,
    isAuthenticated: undefined,
    refreshToken: undefined,
  });

  useEffect(() => {
    authClient.authStateManager.subscribe(setAuthState);
    void authClient.start();

    return () => {
      authClient.authStateManager.unsubscribe(setAuthState);
      void authClient.stop();
    };
  }, []);

  const { data, error, isFetching } = useQuery({
    enabled: authState.isAuthenticated,
    initialData: { email: '', family_name: '', given_name: '', groups: [], sub: '' },
    queryFn: () => authClient.token.getUserInfo(),
    queryKey: ['userInfo'],
    select: (data) => {
      const { email = '', family_name = '', given_name = '', groups, sub } = data;
      return {
        email,
        firstName: given_name,
        groups: (Array.isArray(groups) ? groups : []) as SportybotGroup[],
        id: sub,
        lastName: family_name,
      };
    },
  });

  useEffect(() => {
    if (authState.isAuthenticated) {
      authClient.options.restoreOriginalUri = async (_oktaAuth, originalUri) => {
        await router.invalidate({ sync: true });
        const uri = toRelativeUrl(originalUri ?? '/', window.location.origin);
        await router.navigate({ to: uri });
      };
    }
  }, [authState.isAuthenticated, data]);

  const value = useMemo(() => ({ authClient, authState, user: data }), [authState, data]);

  const waitingForAuth = authState.isAuthenticated === undefined && !authClient.isLoginRedirect();
  const waitingForProfile = authState.isAuthenticated && isFetching;

  if (waitingForAuth || waitingForProfile) {
    return <DefaultPending />;
  }

  if (error) return <p>{error.message}</p>;

  return <AuthContext value={value}>{children}</AuthContext>;
}
